/**
 * Which language the interface speaks, and where that answer comes from.
 *
 * Three sources, in this order: what this browser was **told** to use, what its
 * owner configured in the system, then English. The choice is a property of the
 * browser rather than of the account — the same account is read from a phone and
 * from a desktop, and a language is a property of the screen in front of you.
 *
 * Deliberately free of React so `api/client.ts` can read the language in force
 * without a hook: every request announces it with `Accept-Language`, which is
 * how the server knows which language to write its refusals in.
 */

import { DEFAULT_LOCALE, isLocale, resolveLocale, type Locale } from '@kanto/shared';

const STORAGE_KEY = 'kanto:locale';

/**
 * The explicit choice this browser remembers, `null` when nobody has chosen.
 *
 * Read tolerantly: `localStorage` throws rather than returning null when storage
 * is denied — private browsing in older Safari — and that must fall back to the
 * system language, never stop the page from rendering.
 */
export function readStoredLocale(): Locale | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return isLocale(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function writeStoredLocale(locale: Locale): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Quota exceeded, or writing denied: the choice then lasts for this visit
    // only, which is not worth failing a render over.
  }
}

/**
 * The first supported language among the browser's preferences.
 *
 * `navigator.languages` rather than `navigator.language` alone: somebody whose
 * system runs in a language kanto does not speak usually lists a second one that
 * it does, and reading only the first would send them to English for nothing.
 */
export function browserLocale(): Locale | null {
  const tags = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const tag of tags) {
    const locale = resolveLocale(tag);
    if (locale) return locale;
  }
  return null;
}

/** The language to open with, before anybody touches the switcher. */
export function initialLocale(): Locale {
  return readStoredLocale() ?? browserLocale() ?? DEFAULT_LOCALE;
}

/**
 * The language in force, mirrored outside React.
 *
 * `api/client.ts` runs outside the component tree and cannot call a hook, yet
 * every request has to announce the language: the provider keeps this value in
 * step with its state.
 */
let active: Locale = DEFAULT_LOCALE;

export function activeLocale(): Locale {
  return active;
}

/**
 * Records the language in force and reflects it in the document.
 *
 * `lang` is not decoration: it decides hyphenation and how a screen reader
 * pronounces the page, and `index.html` ships with `lang="en"` — a French
 * interface read aloud in English is exactly what this prevents.
 */
export function setActiveLocale(locale: Locale): void {
  active = locale;
  document.documentElement.lang = locale;
}
